import useCompanyId from '@/hooks/useCompanyId';
import { action } from '@/lib/api.js';
import getCompanyIdForRequest from '@/lib/getCompanyIdForRequest.js';
import setPath from '@/lib/setPath';
import removePref from '@/redux/actions/uiActions/removePref';
import setPref from '@/redux/actions/uiActions/setPref';
import { getUserState, putUserCompany } from '@/redux/actions/userActions';
import clearCredentials from '@/screens/jobs/lib/clearCredentials';
import clearFilters from '@/screens/jobs/lib/clearFilters';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useSelector } from 'react-redux';

const useChangeCompany = () => {
  const { user: clerkUser } = useClerkUser();
  const { setSessionCompanyId } = useCompanyId();
  const userId = useSelector((state) => state?.user?.jwt?.id);

  return (id, opts = { message: 'Please wait while we change companies...', isSignUp: false }, callback) => {
    if (!opts?.isSignUp) {
      setPref('platformLoading', { isLoading: true, message: opts.message, showBeforeUnloadWarning: false });
    }

    // If redirect is true, redirect to either /workflows, /jobs, or /full-service.
    // This prevents stale deep links and clears query params like `home` for Full Service.
    if (opts?.redirect === true) {
      const isInWorkflowScreen = window.location.pathname.includes('/workflows');
      const isInJobScreen = window.location.pathname.includes('/jobs');
      const isInFullServiceScreen = window.location.pathname.includes('/full-service');

      if (isInFullServiceScreen) {
        setPath('/full-service');
      } else {
        const isInRedirectScreen = isInJobScreen || isInWorkflowScreen;
        const newPath = isInWorkflowScreen ? '/workflows' : '/jobs';
        if (isInRedirectScreen && newPath !== window.location.pathname) {
          setPath(newPath);
        }
      }
    }

    setSessionCompanyId(id);
    putUserCompany({ userId, companyId: id }, async () => {
      await clerkUser.reload();
      await callback?.();
      if (!opts?.isSignUp) {
        setTimeout(() => {
          setPref('platformLoading', { isLoading: false, showSpinner: false });
        }, 1000);
      }
    });
    clearFilters();
    clearCredentials();
    // Clear locally now, but only persist the deletion after the selected
    // company's user state supplies its workspace authority.
    action('uiRemovePref', { key: 'copied-steps' });
    getUserState({ userId }, (data, error) => {
      if (error || !data?.workspaceRegion || data?.authorization?.userId !== userId || data?.authorization?.companyId !== id || getCompanyIdForRequest() !== id) {
        return;
      }
      removePref('copied-steps');
    });
  };
};

export default useChangeCompany;
